import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { BarChart3, Eye, MousePointer, Share2, Sparkles, TrendingUp, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getToken() {
  return document.cookie.split('; ').find(r => r.startsWith('session_token='))?.split('=')[1]
    || localStorage.getItem('session_token') || '';
}
const authHeaders = () => ({ Authorization: `Bearer ${getToken()}` });

export default function PerformanceTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/content/performance`, { headers: authHeaders() })
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="perf-loading">
        <Loader2 size={24} className="animate-spin" style={{ color: '#8C7E6E' }} />
      </div>
    );
  }

  const stats = data?.stats || {};
  const topPerformers = data?.top_performers || [];
  const items = data?.items || [];

  const METRICS = [
    { key: 'total_content', label: 'Total Content', value: stats.total_content || 0, icon: BarChart3, color: '#3C3226' },
    { key: 'published', label: 'Published', value: stats.published || 0, icon: TrendingUp, color: '#1D6B4F' },
    { key: 'total_views', label: 'Views', value: stats.total_views || 0, icon: Eye, color: '#B8985A' },
    { key: 'total_clicks', label: 'Clicks', value: stats.total_clicks || 0, icon: MousePointer, color: '#C67A5C' },
    { key: 'total_shares', label: 'Shares', value: stats.total_shares || 0, icon: Share2, color: '#5C5347' },
    { key: 'total_ai_citations', label: 'AI Mentions', value: stats.total_ai_citations || 0, icon: Sparkles, color: '#1D6B4F' },
  ];

  return (
    <div data-testid="performance-tab">
      {/* Metrics grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-8">
        {METRICS.map((m, i) => {
          const Icon = m.icon;
          return (
            <motion.div
              key={m.key}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              className="p-5"
              style={{ backgroundColor: '#F7F4EF', border: '1px solid #DDD5C8' }}
              data-testid={`metric-${m.key}`}
            >
              <div className="flex items-center gap-2 mb-3">
                <div className="w-7 h-7 flex items-center justify-center" style={{ backgroundColor: `${m.color}10` }}>
                  <Icon size={14} style={{ color: m.color }} />
                </div>
                <span className="text-[10px] uppercase tracking-widest" style={{ color: '#8C7E6E' }}>{m.label}</span>
              </div>
              <p className="text-2xl font-light" style={{ color: '#3C3226' }}>{m.value.toLocaleString()}</p>
            </motion.div>
          );
        })}
      </div>

      {/* Top Performers */}
      {topPerformers.length > 0 && (
        <div className="mb-8">
          <p className="text-[10px] uppercase tracking-widest mb-4" style={{ color: '#B8985A' }}>Top Performers</p>
          <div className="space-y-2">
            {topPerformers.map((item, i) => (
              <motion.div
                key={item.content_id}
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.06 }}
                className="flex items-center gap-4 p-4"
                style={{ backgroundColor: '#F7F4EF', border: '1px solid #DDD5C8' }}
                data-testid={`top-performer-${i}`}
              >
                <span className="text-sm font-semibold w-6 text-center" style={{ color: '#B8985A' }}>
                  {i + 1}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate" style={{ color: '#3C3226' }}>{item.title}</p>
                  <div className="flex items-center gap-3 mt-1">
                    <span className="text-[11px]" style={{ color: '#8C7E6E' }}>{item.content_type}</span>
                    <span className="text-[11px]" style={{ color: '#8C7E6E' }}>{item.platform}</span>
                  </div>
                </div>
                <div className="flex items-center gap-4 text-right">
                  <div>
                    <p className="text-sm font-medium" style={{ color: '#3C3226' }}>{item.metrics?.views || 0}</p>
                    <p className="text-[10px]" style={{ color: '#8C7E6E' }}>views</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium" style={{ color: '#1D6B4F' }}>{item.metrics?.ai_citations || 0}</p>
                    <p className="text-[10px]" style={{ color: '#8C7E6E' }}>mentions</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {/* All Content Performance List */}
      {items.length > 0 ? (
        <div>
          <p className="text-[10px] uppercase tracking-widest mb-4" style={{ color: '#B8985A' }}>All Content</p>
          <div className="space-y-2">
            {items.map((item, i) => (
              <div
                key={item.content_id}
                className="flex items-center gap-3 p-3"
                style={{ backgroundColor: '#F7F4EF', border: '1px solid #DDD5C8' }}
                data-testid={`perf-item-${i}`}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-medium truncate" style={{ color: '#3C3226' }}>{item.title}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span
                      className="text-[10px] px-1.5 py-0.5 uppercase tracking-wider font-medium"
                      style={{
                        color: item.status === 'published' ? '#1D6B4F' : '#B8985A',
                        backgroundColor: item.status === 'published' ? '#1D6B4F10' : '#B8985A10',
                      }}
                    >
                      {item.status}
                    </span>
                    <span className="text-[10px]" style={{ color: '#8C7E6E' }}>{item.platform}</span>
                  </div>
                </div>
                <div className="flex items-center gap-3 text-xs" style={{ color: '#5C5347' }}>
                  <span>{item.metrics?.views || 0} views</span>
                  <span>{item.metrics?.clicks || 0} clicks</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <EmptyPerformance />
      )}
    </div>
  );
}

function EmptyPerformance() {
  return (
    <div className="text-center py-16" data-testid="perf-empty">
      <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: '#B8985A10' }}>
        <BarChart3 size={28} strokeWidth={1} style={{ color: '#B8985A' }} />
      </div>
      <h3 className="text-base font-medium mb-2" style={{ color: '#3C3226' }}>No content performance data yet</h3>
      <p className="text-[13px] max-w-xs mx-auto" style={{ color: '#8C7E6E' }}>
        Generate and publish content from the Content tab. Performance metrics will appear here as your content gains traction.
      </p>
    </div>
  );
}
